import { ApiOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client';
import { useLingui } from '@lingui/react';
import { Site } from '@zeus/index';
import { Card, Collapse, DatePicker, Form } from 'antd';
import type { CollapseProps } from 'antd';
import type { FormInstance } from 'antd/es/form';
import dayjs, { Dayjs } from 'dayjs'; // dayjs,
import { useEffect, useState, useRef } from 'react';
import { styled } from 'styled-components';
import {
  GQL_COMMON_STATS,
  StoredTrackingCommonStats,
  StoredRushHour,
} from '~/gql/tracking-thematique/tracking-thematique';
import { authClient } from '~/helpers/apollo';
import Chart from '~/pages/backoffice/admin/stats/Chart';
import { Loading } from '~/pages/components/ui/loading';

// copied from rc-picker/lib/interface.d.ts
declare type EventValue<DateType> = DateType | null;
declare type RangeValue<DateType> =
  | [EventValue<DateType>, EventValue<DateType>]
  | null;

const layout = {
  labelCol: { span: 8 },
  wrapperCol: { span: 16 },
};

function StatsPage() {
  const { i18n } = useLingui();
  const { RangePicker } = DatePicker;
  const formRef = useRef<FormInstance>(null);
  const [form] = Form.useForm();
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(false);
  const [stats, setStats] = useState<StoredTrackingCommonStats | null>(null);
  const [passedTime, setPassedTime] = useState<number>(0);
  const [rushHoursIdeev, setRushHoursIdeev] = useState<StoredRushHour[]>([]);
  const [rushHoursHm, setRushHoursHm] = useState<StoredRushHour[]>([]);

  const items: CollapseProps['items'] = [
    {
      key: '1',
      label: i18n._('stats.influx'),
      children: (
        <>
          <SDiv>
            <Chart rushHoursHm={rushHoursHm} rushHoursIdeev={rushHoursIdeev} />
          </SDiv>
        </>
      ),
    },
    // Ajouter peut-être ici ? ou sur une page à part pour plus de lisibilité
    // {
    //   key: '1',
    //   label: i18n._('stats.trackings'),
    //   children: (
    //     <>
    //       <SDiv>trackings</SDiv>
    //     </>
    //   ),
    // },
  ];

  const { refetch: fetchStats } = useQuery(GQL_COMMON_STATS, {
    variables: {
      startDate: startDate?.setHours(0, 0, 0, 0),
      endDate: endDate?.setHours(23, 59, 59, 0),
    },
    client: authClient,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      setStats(data.getStats);
      setPassedTime(Math.round(data.getStats.passedTime / 60)); // pour voir en min
      const dataIdeev = data.getStats.siteRushHour.find(
        (el) => el.site === Site.IDEEV
      )?.rushHours;
      setRushHoursIdeev(dataIdeev ?? []);

      const dataHm = data.getStats.siteRushHour.find(
        (el) => el.site === Site.HM
      )?.rushHours;
      setRushHoursHm(dataHm ?? []);
    },
    onError: (e) => {
      console.log('erreur dans GQL_COMMON_STATS', e);
    },
  });

  async function getStats() {
    setLoading(true);
    await fetchStats();
    setLoading(false);
  }

  useEffect(() => {
    void getStats();
  }, []);

  useEffect(() => {
    formRef.current?.resetFields();
  }, []);

  const onRangeChange = (values: RangeValue<Dayjs>) => {
    if (values) {
      setStartDate(values[0]?.toDate());
      setEndDate(values[1]?.toDate());
    } else {
      setStartDate(undefined);
      setEndDate(undefined);
    }

    void getStats();
  };

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <>
          <Form
            {...layout}
            ref={formRef}
            name="trackings-range"
            form={form}
            style={{ margin: '0' }}
          >
            <SDivFilterElement>
              <Form.Item
                name="range"
                label={i18n._('filter.period')}
                style={{ margin: '0' }}
              >
                <RangePicker
                  onChange={onRangeChange}
                  value={[
                    dayjs(startDate as unknown as string),
                    dayjs(endDate as unknown as string),
                  ]}
                />
              </Form.Item>
            </SDivFilterElement>
          </Form>
          <SDiv>
            <Card title={i18n._('stats.connections')} style={{ width: 300 }}>
              <SCardContent>
                <SIconSpan>
                  <ApiOutlined />
                </SIconSpan>
                <span>
                  {stats?.connectionCount} {i18n._('stats.connection.label')}
                </span>
              </SCardContent>
            </Card>
            <Card title={i18n._('stats.passedTime')} style={{ width: 300 }}>
              <SCardContent>
                <SIconSpan>
                  <ClockCircleOutlined />
                </SIconSpan>
                <span>{passedTime} min</span>
              </SCardContent>
            </Card>
          </SDiv>
          <Collapse defaultActiveKey={['1']} items={items} />
        </>
      )}
    </>
  );
}

export default StatsPage;

export const SDivFilterElement = styled.div`
  align-items: center;
  display: flex;
  margin-top: 20px;
  width: 100%;

  & > *:first-of-type {
    margin-right: 10px;
  }
`;

const SDiv = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-around;
  margin: 20px 0;
  width: 100%;
`;

const SIconSpan = styled.span`
  font-size: 1.5rem;
  margin-right: 10px;
`;

const SCardContent = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  width: 100%;
`;
